import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { getThemeName, loadTheme } from "./config/themeConfig";
import { Content, Layout, Result, message } from "./assets/imports/antdComponents";
import {
	closeForbiddenModal,
	setAllNotesTemplates,
	setAllStudiesData,
	setAllStudyTypes,
	setAllWebBoardsTemplates,
	setAvailableLanguages,
	setDropdownMaster,
	setFeatureList,
	setInterfaceLanguage,
	setMetaDetails,
	setRole,
	setSelectedStudy,
	setSupportedLanguages,
	setTaxonomyDropdownData,
	setUserProfile,
	setUserProfileId,
} from "./pages/Studies/states/studiesSlice";
import { setManageStudyDetails } from "./pages/ManageStudies/states/slice/manageStudySlice";
import { setSelectedTheme, setSelectedThemeName } from "./pages/AdminCenter/states/themeManagementSlice";
import { getAllStudiesDetails, getDropdownValues, getMetaDetails, invalidateDropdownInFlight, invalidateMetaInFlight, invalidateStudiesInFlight, sendTrackingData } from "./pages/Studies/serviceHelper";
import { readProfileCache, writeProfileCache } from "./services/userProfileCache";
import { processIconPath, processIconPath2, transformCustomTags } from "./pages/Studies/transformer";
import { environment } from "./config/environment";
import { accessErrorTypes, FeatureKeysType, IResponse, roleAccessType, storyType, SupportedLanguageType, IStudyData, ModuleTypes } from "./pages/Studies/models/interfaces";
import { useFeatures } from "./config/featureManager";
import { resetAllChartsCacheBasic, resetAllChartsSummaryCacheBasic } from "./pages/Insights/states/sectionalSummarySlice";
import RoutesConfig from "./config/routesConfig";
import TopNavbar from "./components/Navbar/TopNavbar";
import SideNavbar from "./components/Navbar/SideNavbar";
import AppLicenseExpiryBanner from "./components/AppLicenseExpiry";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthScreen from "./components/AutenticatingComp";
const NewReleasePopup = lazy(() => import(/* webpackChunkName: "new-release-popup" */ "./components/NewReleasePopup"));
const InfoModal = lazy(() => import(/* webpackChunkName: "info-modal" */ "./components/InfoModal"));
import i18n from "./i18n";
import { getAllTaxonomy } from "./pages/Taxanomy/serviceHelper";
import { useParams } from "react-router-dom";
import { setIsUserGuideModalVisible, setSelectedArticle, setSharedArticleId } from "./pages/UserGuide/states/userGuideSlice";
import { clearUserActivityData } from "./components/UserMonitoring/states/userActivitySlice";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { setActiveFeatures } from "./pages/NewAskKural/states/askKuralSlice";
import { getRememberedUser, clearRememberedUser, setRememberedUser } from "./utils/rememberedUser";
import { clearCognitoToken, revokeCognitoSession } from "./services/cognitoAuth";
import { startInteractiveCognitoLogin } from "./services/cognitoAutoLogin";
import { resolveProviderForEmail } from "./config/loginMethods";
import DynamicLogo from "src/components/DynamicLogo";
import "./pages/Login/style.css";

const AppContent: React.FC = () => {
	const PROFILE_CACHE_IDENTITY_KEY = "kural-profile-cache-identity";
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const { initializeFeatures, isFeatureAvailable } = useFeatures();
	const { studiesData, selectedStudy, dropdownMaster, userProfile, userProfileId, isForbiddenModalOpen, role, isForbiddenMessage, accessErrorType, supportedLanguages, isNavDisabled } = useSelector((state: RootState) => state.studiesSlice);
	const { isThemeManagementOpen, selectedTheme } = useSelector((state: RootState) => state.themeManagementSlice);
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [profile, setProfile] = useState<boolean>(true);
	const [error, setError] = useState(false);
	const [isReleaseNoteModalOpen, setIsReleaseNoteModalOpen] = useState<boolean>(false);
	const [isMetaLoading, setIsMetaLoading] = useState(false);
	const [isStudiesLoading, setIsStudiesLoading] = useState(false);
	const [isProcessingUserGuide, setIsProcessingUserGuide] = useState(false);
	const [hasStudiesFetched, setHasStudiesFetched] = useState(false);
	const [accountMismatch, setAccountMismatch] = useState<{ intended: string; actual: string } | null>(null);

	const isUserGuideRoute = useMemo(() => location.pathname.match(/^\/user-guide\/(.+)$/), [location.pathname]);
	const { userActivityData } = useSelector((state: RootState) => state.userActivitySlice);
	const isUserActivityMonitoring = isFeatureAvailable(FeatureKeysType.UserActivityMonitoring);
	const isNotesEnabled = isFeatureAvailable(FeatureKeysType.Notes);
	const [areFeaturesInitialized, setAreFeaturesInitialized] = useState(false);
	const adminRoutes = ["/admin-center", "/feature-management", "/event-logs"];

	// Use ref to access latest data without triggering effect
	const activityDataRef = useRef(userActivityData);
	const { instance, inProgress } = useMsal();

	// Update ref when data changes (doesn't trigger effect)
	useEffect(() => {
		activityDataRef.current = userActivityData;
	}, [userActivityData]);

	const isMountedRef = useRef(true);
	// Set true immediately before handleUserAuthentication applies a meta response; the [role, userProfile] effect
	// skips one refetch so we don't loop. Cleared in the effect, or via setTimeout if no role/profile update fired the effect.
	const suppressRoleProfileMetaEffectRef = useRef(false);
	// Holds role/profileId hydrated from localStorage; reconciled against meta's authoritative values.
	const hydratedCacheRef = useRef<{ role: string; userProfileId: string } | null>(null);
	// Closure refs kept in sync with latest state so useCallback-stable fetchers can read fresh values.
	const handleUserAuthRef = useRef<(u: any) => Promise<void> | void>();
	const initializeFeaturesRef = useRef<(featuresOverride?: any[]) => void>();
	// Incremented by invalidateDropdownInFlight; fetchDropdownValues captures the id at call-time and discards the response if the id has changed by the time it resolves (stale-cache race guard).
	const dropdownRequestIdRef = useRef(0);
	// Incremented when role/profile changes so stale metadata responses cannot repopulate Redux with the previous role's feature mapping.
	const metaRequestIdRef = useRef(0);
	const isAdmin = role === roleAccessType.Admin;
	const isSupport = role === roleAccessType.Support;
	const isDeveloper = role === roleAccessType.Developer;
	const isAdminOrSupport = isAdmin || isSupport;
	const isObserver = role === roleAccessType.Observer;
	const isRestrictedRole = isAdmin || isSupport || isDeveloper || isObserver;

	const isCopyrightEnabled = isFeatureAvailable(FeatureKeysType.Copyright);
	// Routes where the Studies page mounts and fetches folder/studies itself, making AppContent's fetchAllStudies a duplicate.
	const isOnStudiesPage =
		location.pathname === "/" || location.pathname === "/studies" || location.pathname === "/add-study";

	const resolveCacheIdentity = (loggedInUser?: any) => {
		const msalUpn = instance.getActiveAccount()?.username || instance.getAllAccounts()[0]?.username;
		const fallbackEmail = loggedInUser?.user_email;
		const cachedIdentity = localStorage.getItem(PROFILE_CACHE_IDENTITY_KEY);
		return msalUpn || fallbackEmail || cachedIdentity;
	};

	const persistCacheIdentity = (identity: string | null | undefined) => {
		if (identity) {
			localStorage.setItem(PROFILE_CACHE_IDENTITY_KEY, identity);
		}
	};

	const handleReleaseNotes = () => {
		const current_version = environment.REACT_APP_RELEASE_NOTE_VERSION;
		if (!current_version) {
			localStorage.removeItem("KuralReleaseNoteDisplay");
			return;
		}
		const storedData = localStorage.getItem("KuralReleaseNoteDisplay");
		if (!storedData) {
			initializeReleaseNotes(current_version);
			return;
		}
		const { version, data } = JSON.parse(storedData);
		if (version !== current_version) {
			initializeReleaseNotes(current_version);
			return;
		}
		if (!data.hasCancelled) {
			checkTimeAndShowModal(data.loginTime);
		}
	};

	const initializeReleaseNotes = (version: string) => {
		const newData = {
			version,
			data: {
				loginTime: new Date().toISOString(),
				hasCancelled: false,
			},
		};
		localStorage.setItem("KuralReleaseNoteDisplay", JSON.stringify(newData));
		setIsReleaseNoteModalOpen(true);
	};

	const checkTimeAndShowModal = (loginTime: string) => {
		const timeDifference = new Date().getTime() - new Date(loginTime).getTime();
		const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
		if (timeDifference < twoDaysInMs) {
			setIsReleaseNoteModalOpen(true);
		}
	};

	// Concurrent calls are collapsed by the in-flight cache in serviceHelper; Redux dispatches are idempotent.
	const fetchAllStudies = useCallback(async () => {
		setIsStudiesLoading(true);
		try {
			const response: any = await getAllStudiesDetails();
			if (isMountedRef.current) {
				if (response?.data) {
					const { folders = [], untracked = [] } = response.data;
					const allStudiesArray: IStudyData[] = [...folders.flatMap((folder: any) => folder.studies || []), ...untracked];
					dispatch(setAllStudiesData(allStudiesArray));
					dispatch(resetAllChartsCacheBasic(allStudiesArray));
					dispatch(resetAllChartsSummaryCacheBasic(allStudiesArray));
				}
			}
		} catch (error) {
			console.error("Error:", error);
		} finally {
			if (isMountedRef.current) {
				setIsStudiesLoading(false);
				setHasStudiesFetched(true);
			}
		}
	}, [dispatch]);

	// Reads closures via refs to stay stable without deps; in-flight cache makes the loading guard unnecessary at the network layer.
	const fetchMetaDetails = useCallback(async (isRetry = false) => {
		const requestId = metaRequestIdRef.current;
		setIsMetaLoading(true);
		try {
			const response: any = await getMetaDetails();
			if (metaRequestIdRef.current !== requestId || !response?.data || !isMountedRef.current) return;
			const loggedInUser = response.data.logged_in_user;
			const featureMapping = loggedInUser.feature_mapping || [];

			dispatch(setActiveFeatures(featureMapping));
			dispatch(setFeatureList(loggedInUser.feature_announcements || []));
			initializeFeaturesRef.current?.(featureMapping);
			if (isRetry) {
				navigate("/");
				window.location.reload();
			}
			suppressRoleProfileMetaEffectRef.current = true;
			handleUserAuthRef.current?.(loggedInUser);
		} catch (error: any) {
			if (metaRequestIdRef.current !== requestId) return;
			console.error("Error:", error);
			if (isMountedRef.current) {
				if ((error.response?.status === 500 || error.response?.status === 403) && !isRetry) {
					setProfile(null);
					dispatch(setUserProfile(null));
					dispatch(setUserProfileId(null));
					dispatch(setRole(null));

					await new Promise((resolve) => setTimeout(resolve, 0));
					return fetchMetaDetails(true);
				}

				if (error.response?.status === 401) {
					setIsAuthenticated(false);
				} else {
					setError(true);
				}
			}
		} finally {
			if (isMountedRef.current && metaRequestIdRef.current === requestId) {
				setIsMetaLoading(false);
			}
			// If meta did not change role/profile, the effect may not run — avoid leaving suppress stuck true.
			if (metaRequestIdRef.current === requestId) {
				setTimeout(() => {
					if (suppressRoleProfileMetaEffectRef.current) {
						suppressRoleProfileMetaEffectRef.current = false;
					}
				}, 0);
			}
		}
	}, [dispatch, navigate]);

	const navigateToAdminCenterIfRequired = useCallback(
		(selectedRole: string) => {
			const isAdminRole = selectedRole === roleAccessType.Admin || selectedRole === roleAccessType.Support;
			const isDeveloperRole = selectedRole === roleAccessType.Developer;
			const isObserverRole = selectedRole === roleAccessType.Observer;
			const isOnAdminRoute = adminRoutes.some((route) => location.pathname.startsWith(route));
			const isRootOrStudiesRoute = location.pathname === "/" || location.pathname === "/studies";

			if (isAdminRole && (isRootOrStudiesRoute || !isOnAdminRoute)) {
				navigate("/admin-center", { replace: true });
				return;
			}

			if (isDeveloperRole && isRootOrStudiesRoute) {
				navigate("/manage-api-keys", { replace: true });
				return;
			}

			if (isObserverRole && location.pathname !== "/monitoring") {
				navigate("/monitoring", { replace: true });
			}
		},
		[location.pathname, navigate],
	);

	const checkAccountMismatch = async (loggedInUser: any): Promise<boolean> => {
		const remembered = getRememberedUser();
		if (remembered && loggedInUser.user_email && remembered.email.toLowerCase() !== loggedInUser.user_email.toLowerCase()) {
			setAccountMismatch({ intended: remembered.email, actual: loggedInUser.user_email });
			await revokeCognitoSession();
			return true;
		}
		clearRememberedUser();
		return false;
	};

	const handleUserAuthentication = async (loggedInUser: any) => {
		if (!loggedInUser) {
			setProfile(false);
			setIsAuthenticated(false);
			return;
		}
		dispatch(setMetaDetails(loggedInUser));
		const rolesMapping = loggedInUser.roles || [];
		const profileLanguageMapping = loggedInUser.profile_language_mapping || [];
		const hasAdminOrSupport = rolesMapping.includes(roleAccessType.Admin) || rolesMapping.includes(roleAccessType.Support);

		if (rolesMapping.length === 0) {
			setProfile(false);
			setIsAuthenticated(false);
			return;
		}

		// Handle users with no profile mapping
		if (profileLanguageMapping.length === 0) {
			// The backend owns the "which roles legitimately need no profile" decision
			// (admin family + developer → PROFILE_EXEMPT_ROLES) and surfaces it as
			// `allows_no_profile`. Trust that signal first. The role-based check is a
			// backward-compatible fallback for older metadata payloads and must mirror
			// the backend exempt set — including Developer, which was previously missing.
			const isProfileExemptRole =
				hasAdminOrSupport ||
				rolesMapping.includes(roleAccessType.Observer) ||
				rolesMapping.includes(roleAccessType.Developer);
			const allowsNoProfile = Boolean(loggedInUser?.allows_no_profile) || isProfileExemptRole;
			if (!allowsNoProfile) {
				setProfile(false);
				setIsAuthenticated(false);
				return;
			}
			const selectedRole = rolesMapping.includes(roleAccessType.Admin)
				? roleAccessType.Admin
				: rolesMapping.includes(roleAccessType.Support)
					? roleAccessType.Support
					: rolesMapping.includes(roleAccessType.Developer)
						? roleAccessType.Developer
						: rolesMapping.includes(roleAccessType.Observer)
							? roleAccessType.Observer
							: rolesMapping[0];

			// TODO: remove after backend grants Observer API access
			const dispatchedRole = selectedRole === roleAccessType.Observer ? roleAccessType.Admin : selectedRole;
			dispatch(setRole(dispatchedRole));
			const cacheIdentity = resolveCacheIdentity(loggedInUser);
			const resolvedProfileId = loggedInUser?.current_profile_id || "";
			const resolvedProfile =
				loggedInUser?.profile_language_mapping?.find((p: any) => p?.profile_id === resolvedProfileId)?.profile_name ||
				loggedInUser?.profiles?.[0] ||
				"";

			// Persist only when metadata provides a concrete profile-id; readProfileCache requires it.
			if (resolvedProfileId && cacheIdentity) {
				persistCacheIdentity(cacheIdentity);
				writeProfileCache(cacheIdentity, {
					role: selectedRole,
					userProfile: resolvedProfile,
					userProfileId: resolvedProfileId,
				});
			}

		if (await checkAccountMismatch(loggedInUser)) return;

		setIsAuthenticated(true);
		navigateToAdminCenterIfRequired(selectedRole); // still passes Observer so routing goes to /monitoring
		return;
	}

	// Normal flow for users with profile mapping
	const roleExists = rolesMapping.includes(role);
	const selectedRole = roleExists ? role : rolesMapping[0];
	dispatch(setRole(selectedRole));
	setupUserProfile(loggedInUser);

	if (await checkAccountMismatch(loggedInUser)) return;

		setIsAuthenticated(true);
		navigateToAdminCenterIfRequired(selectedRole);

		const hasRestrictedRole = hasAdminOrSupport || rolesMapping.includes(roleAccessType.Developer);

		// Resolve the final profileId the same way setupUserProfile does, so cache write and reconciliation stay consistent.
		const profileExists = loggedInUser.profile_language_mapping.some(
			(p: any) => p.profile_name === userProfile && p.profile_id === userProfileId,
		);
		const resolvedProfile = profileExists ? userProfile : loggedInUser?.profile_language_mapping[0]?.profile_name;
		const resolvedProfileId = profileExists ? userProfileId : loggedInUser?.profile_language_mapping[0]?.profile_id;

		const cacheIdentity = resolveCacheIdentity(loggedInUser);
		if (cacheIdentity && resolvedProfileId) {
			persistCacheIdentity(cacheIdentity);
			writeProfileCache(cacheIdentity, {
				role: selectedRole,
				userProfile: resolvedProfile,
				userProfileId: resolvedProfileId,
			});
		}

		// If the hydrated cache was stale, preemptive calls went out with wrong headers — invalidate and refetch.
		const hydrated = hydratedCacheRef.current;
		hydratedCacheRef.current = null;
		const cacheWasStale =
			hydrated && (hydrated.role !== selectedRole || hydrated.userProfileId !== resolvedProfileId);
		if (cacheWasStale) {
			invalidateStudiesInFlight();
			dispatch(setAllStudiesData([]));
			setHasStudiesFetched(false);
			// Dropdown fired with stale role-id header — invalidate the in-flight request and bump the request id so any already-resolved parallel response is discarded before Redux dispatch.
			invalidateDropdownInFlight();
			dropdownRequestIdRef.current += 1;
			dispatch(setDropdownMaster([]));
		}

		if (!hasRestrictedRole && !isOnStudiesPage && (studiesData.length === 0 || cacheWasStale)) {
			await fetchAllStudies();
		}
	};

	// Point refs at this render's closures so stable callbacks always invoke the latest version.
	handleUserAuthRef.current = handleUserAuthentication;
	initializeFeaturesRef.current = initializeFeatures;

	const setupUserProfile = (loggedInUser: any) => {
		setupLanguageSettings(loggedInUser, role);
		const profileExists = loggedInUser.profile_language_mapping.some((profile: any) => profile.profile_name === userProfile && profile.profile_id === userProfileId);
		const profile = profileExists ? userProfile : loggedInUser?.profile_language_mapping[0]?.profile_name;
		const profileId = profileExists ? userProfileId : loggedInUser?.profile_language_mapping[0]?.profile_id;
		dispatch(setUserProfile(profile));
		dispatch(setUserProfileId(profileId));
	};

	const setupLanguageSettings = (loggedInUser: any, role: any) => {
		const isEnglishOnlyRole = role === roleAccessType.Admin || role === roleAccessType.Support || role === roleAccessType.Developer;

		if (isEnglishOnlyRole) {
			i18n.changeLanguage(SupportedLanguageType.English);
			localStorage.setItem("i18nextLng", SupportedLanguageType.English);
			dispatch(setAvailableLanguages([{ code: SupportedLanguageType.English, name: "English" }]));
			return;
		}

		const languages = loggedInUser.languages || [];
		dispatch(setAvailableLanguages(languages));

		const storedLanguage = localStorage.getItem("i18nextLng");
		const interfaceLanguage = languages?.find((lang: any) => lang?.code === storedLanguage) || languages?.[0] || { name: "English", code: SupportedLanguageType.English };

		if (languages.length > 0 && interfaceLanguage) {
			i18n.changeLanguage(interfaceLanguage.code);
			localStorage.setItem("i18nextLng", interfaceLanguage.code);
			dispatch(setSupportedLanguages(Array.from(new Set([SupportedLanguageType.English, interfaceLanguage.code]))));
			dispatch(setInterfaceLanguage(interfaceLanguage));
		}
	};

	const fetchDropdownValues = async () => {
		// Capture the current request id at call-time. If invalidateDropdownInFlight is called while the request is in-flight (stale cache race), the id will have been incremented and we discard the response rather than overwriting Redux with stale data.
		const capturedRequestId = dropdownRequestIdRef.current;
		try {
			const response: any = await getDropdownValues();
			// Discard if a newer request cycle has started (cache was invalidated mid-flight).
			if (dropdownRequestIdRef.current !== capturedRequestId) return;
			if (response?.data) {
				const transformedData = {
					...response.data,
					custom_tags: transformCustomTags(response.data.custom_tags),
				};
				const studyTypes = response?.data?.study_type
					.filter((type: any) => type?.key !== null && type?.key !== undefined)
					.map((type: any) => ({
						value: type?.value,
						key: type?.key,
						label: type?.label,
						display_name: type?.display_name,
						metadata: type?.metadata,
						icon: processIconPath(type?.key),
						icon2: processIconPath2(type?.key),
						ask_kural_display_name: type?.ask_kural_display_name,
					}));
				dispatch(setAllStudyTypes(studyTypes));
				dispatch(setAllNotesTemplates(response?.data?.notes_template));
				dispatch(setAllWebBoardsTemplates(response?.data?.web_board_template));
				dispatch(setDropdownMaster(transformedData));
			}
		} catch (error) {
			console.error("Error:", error);
		}
	};

	const getAllTaxonomyData = async () => {
		try {
			const response: IResponse = await getAllTaxonomy(false);
			if (response) dispatch(setTaxonomyDropdownData(response.data));
		} catch (err) {
			if (err.response?.status === 404) {
				console.error(t("No taxonomy data found"));
			}
		}
	};

	useEffect(() => {
		dispatch(setSelectedThemeName(getThemeName()));
		const loadedThemeData = loadTheme(getThemeName());
		dispatch(setSelectedTheme(loadedThemeData));
		document.title = environment.REACT_APP_DOCUMENT_TITLE || "Kural";

		// Hydrate role/profile from localStorage so axios headers are correct before meta resolves.
		const cacheIdentity = resolveCacheIdentity();
		const cached = readProfileCache(cacheIdentity);

		if (cached) {
			dispatch(setRole(cached.role));
			dispatch(setUserProfile(cached.userProfile));
			dispatch(setUserProfileId(cached.userProfileId));
			if (cached.supportedLanguages) {
				dispatch(setSupportedLanguages(cached.supportedLanguages));
			}
			hydratedCacheRef.current = { role: cached.role, userProfileId: cached.userProfileId };

			const cachedIsRestricted =
				cached?.role === roleAccessType.Admin ||
				cached?.role === roleAccessType.Support ||
				cached?.role === roleAccessType.Developer;

			if (!cachedIsRestricted) {
				// Fire studies and dropdown in parallel with meta using the hydrated cache headers. fetchDropdownValues captures dropdownRequestIdRef at call-time; if meta later finds the cache was stale it increments the ref via invalidateDropdownInFlight, causing the in-flight dropdown response to be discarded safely before any Redux dispatch.
				const parallelCalls: Promise<unknown>[] = [fetchMetaDetails()];
				if (!isOnStudiesPage) {
					parallelCalls.push(fetchAllStudies());
				}
				parallelCalls.push(fetchDropdownValues());
				// Fire-and-forget — each function handles its own errors internally.
				void Promise.allSettled(parallelCalls);
				handleReleaseNotes();
				return;
			}
		}

		// No cache or restricted role — meta only; dropdown owned by post-auth useEffect.
		void fetchMetaDetails();

		handleReleaseNotes();
	}, []);

	useEffect(() => {
		if (!isAuthenticated || isMetaLoading) return;
		if (suppressRoleProfileMetaEffectRef.current) {
			suppressRoleProfileMetaEffectRef.current = false;
			return;
		}
		metaRequestIdRef.current += 1;
		invalidateMetaInFlight();
		setHasStudiesFetched(false);
		// Clear stale features immediately so the loading-state guard in consumers (e.g. StudyMetadata)
		// treats the transition window as "not yet loaded" rather than evaluating the old role's features.
		dispatch(setActiveFeatures([]));
		fetchMetaDetails();
	}, [role, userProfile, isAuthenticated, fetchMetaDetails]);

	// Single owner of dropdown + taxonomy fetching. Fires once after auth is confirmed. Intentionally does NOT run at mount time to prevent a stale-cache race with invalidateDropdownInFlight.
	useEffect(() => {
		if (isAuthenticated && dropdownMaster?.length === 0 && !isRestrictedRole) {
			// Defer until idle so React's render pipeline runs first; setTimeout is a Safari fallback.
			const schedule = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 200));
			schedule(() => {
				const parallelCalls: Promise<unknown>[] = [fetchDropdownValues()];
				if (isNotesEnabled) {
					parallelCalls.push(getAllTaxonomyData());
				}
				// Fire-and-forget; individual functions handle their own errors.
				void Promise.allSettled(parallelCalls);
			});
		}
	}, [isAuthenticated, dropdownMaster, isRestrictedRole, isNotesEnabled]);

	useEffect(() => {
		const shouldFetchStudies =
			isAuthenticated &&
			userProfile &&
			location.pathname !== "/" &&
			location.pathname !== "/studies" &&
			location.pathname !== "/add-study" &&
			studiesData.length === 0 &&
			!hasStudiesFetched &&
			!isRestrictedRole;
		if (shouldFetchStudies && !isStudiesLoading && !isMetaLoading) {
			// Meta is already driven by mount + role/profile switches; refetching it here would re-trigger studies.
			fetchAllStudies();
		}
	}, [isAuthenticated, userProfile, location.pathname, studiesData.length, isRestrictedRole, isStudiesLoading, isMetaLoading, hasStudiesFetched]);

	useEffect(() => {
		if (!isForbiddenModalOpen && (accessErrorType === accessErrorTypes.COLLABORATOR_REMOVED_VIEW || accessErrorType === accessErrorTypes.FEATURE_DISABLED)) {
			dispatch(setSelectedStudy(null));
			dispatch(setManageStudyDetails({}));
			if (accessErrorType === accessErrorTypes.FEATURE_DISABLED) {
				fetchMetaDetails();
			}
			navigate("/");
		}
	}, [isForbiddenModalOpen, accessErrorType]);

	useEffect(() => {
		if (activeFeatures?.length > 0) {
			initializeFeatures();
			setAreFeaturesInitialized(true);
		}
	}, [activeFeatures]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const NETWORK_MSG_KEY = "network-connection-lost";

		const handleOffline = () => {
			message.error({
				content: "Network connection lost. Will reconnect when available.",
				key: NETWORK_MSG_KEY,
				duration: 10,
			});
		};

		const handleOnline = () => {
			message.success({
				content: "Network connection restored.",
				key: NETWORK_MSG_KEY,
			});
		};

		if (!navigator.onLine) handleOffline();

		window.addEventListener("offline", handleOffline);
		window.addEventListener("online", handleOnline);

		return () => {
			window.removeEventListener("offline", handleOffline);
			window.removeEventListener("online", handleOnline);
		};
	}, []);

	useEffect(() => {
		if (!isAuthenticated || isMetaLoading || !isUserGuideRoute) return;

		const articleId = decodeURIComponent(isUserGuideRoute[1]);
		dispatch(setSharedArticleId(articleId));
		dispatch(setIsUserGuideModalVisible(true));
		navigate("/");

		setTimeout(() => setIsProcessingUserGuide(false), 100);
	}, [location.pathname, isAuthenticated, isMetaLoading]);

	useEffect(() => {
		if (!isAuthenticated || isMetaLoading || !areFeaturesInitialized) return;

		const isRootOrStudiesRoute = location.pathname === "/" || location.pathname === "/studies";

		if (isAdminOrSupport && isRootOrStudiesRoute) {
			navigate("/admin-center", { replace: true });
		} else if (isDeveloper && location.pathname !== "/manage-api-keys") {
			navigate("/manage-api-keys", { replace: true });
		} else if (isObserver && location.pathname !== "/monitoring") {
			navigate("/monitoring", { replace: true });
		} else if (!isRestrictedRole && adminRoutes.some((route) => location.pathname.startsWith(route))) {
			navigate("/");
		}
	}, [role, isAuthenticated, isMetaLoading, areFeaturesInitialized, isRestrictedRole, isDeveloper, isObserver, location.pathname]);

	if (isUserGuideRoute && isAuthenticated) {
		return null;
	}

	if (accountMismatch) {
		const handleContinueAsCurrent = async () => {
			await revokeCognitoSession();
			setRememberedUser({ email: accountMismatch.actual, name: accountMismatch.actual });
			setAccountMismatch(null);
			const provider = resolveProviderForEmail(accountMismatch.actual);
			await startInteractiveCognitoLogin(provider);
		};
		const handleSwitchAccount = async () => {
			clearRememberedUser();
			setAccountMismatch(null);
			const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
			window.location.href = `${basePath}/login`;
		};
		return (
			<div className="login-page scale-baseline-100">
				<div className="login-page__card" role="main">
					<div className="login-page__brand login-page__stagger login-page__stagger--1">
						<DynamicLogo redirectToHome={() => {}} />
					</div>

					<h2 className="login-page__context-text login-page__stagger login-page__stagger--2">
						{t("Account mismatch")}
					</h2>

					<p className="login-page__subtext login-page__stagger login-page__stagger--2">
						{t("You entered {{intended}}. But you are authenticated as {{actual}}. Would you like to continue?", {
							actual: accountMismatch.actual,
							intended: accountMismatch.intended,
						})}
					</p>

					<button
						type="button"
						onClick={handleContinueAsCurrent}
						className="login-page__btn-sso login-page__stagger login-page__stagger--3"
					>
						<i className="material-symbols-outlined login-page__btn-icon" aria-hidden="true">
							person
						</i>
						{t("Continue as {{email}}", { email: accountMismatch.actual })}
					</button>

					<button
						type="button"
						onClick={handleSwitchAccount}
						className="login-page__zs-link login-page__stagger login-page__stagger--4"
					>
						{t("Sign out and switch accounts")}
					</button>
				</div>
			</div>
		);
	}

	if (!isAuthenticated) return <AuthScreen profile={profile} />;
	if (error) return <Result status="500" title="" subTitle={t("Sorry, something went wrong.")} />;

	// Hard 403s (account disabled, access expired, no roles, etc.) take over the entire
	// Layout — no navbar, no routes, no dismiss. Determined by `isNavDisabled`, which the
	// `openForbiddenModal` reducer sets for terminal error types. Soft 403s
	// (`COLLABORATOR_REMOVED_*`, `INSUFFICIENT_PERMISSIONS`, `FEATURE_DISABLED`) keep the
	// existing dismissible `InfoModal` flow below.
	if (isNavDisabled) {
		const isAppAccessExpired = accessErrorType === accessErrorTypes.APP_ACCESS_EXPIRED;
		return (
			<ErrorBoundary resetKeys={[location.pathname]}>
				<Layout>
					<div className="flex flex-col justify-center items-center min-h-screen bg-white text-center scale-baseline-100">
						{isAppAccessExpired ? (
							<Result
								status="403"
								title={t("Application access expired")}
								subTitle={t("Your access to this application has expired. Please renew your license to restore access.")}
							/>
						) : (
							<Result status="403" title="" subTitle={t(isForbiddenMessage)} />
						)}
					</div>
				</Layout>
			</ErrorBoundary>
		);
	}

	return (
		<ErrorBoundary resetKeys={[location.pathname]}>
			<Layout>
				<AppLicenseExpiryBanner />
				{isReleaseNoteModalOpen && (
					<Suspense fallback={null}>
						<NewReleasePopup isModalOpen={isReleaseNoteModalOpen} setIsModalOpen={setIsReleaseNoteModalOpen} />
					</Suspense>
				)}
				{isForbiddenModalOpen && (
					<Suspense fallback={null}>
						<InfoModal
							isModalOpen={isForbiddenModalOpen}
							closeModal={() => dispatch(closeForbiddenModal())}
							title={t("Access Forbidden")}
							bodyContent={isForbiddenMessage}
							status="error"
							centered={true}
							width={416}
							footerContent={null}
						/>
					</Suspense>
				)}
				{selectedTheme?.isSideNavbarPrimary ? <PrimarySidebarLayout /> : <PrimaryTopNavLayout />}
				{isCopyrightEnabled && (
					<div className="copyright-text">
						© Copyright 2026, ZS Associates, Inc. – ZS Confidential Information
					</div>
				)}
			</Layout>
		</ErrorBoundary>
	);
};

const PrimarySidebarLayout: React.FC = () => {
	const location = useLocation();
	const isInsilicoPage = location.pathname.startsWith("/personas");

	return (
		<>
			<Layout>
				{!isInsilicoPage && <SideNavbar />}
				<Layout>
					<TopNavbar />
					<Layout>
						<Content className="app-content">
							<RoutesConfig />
						</Content>
					</Layout>
				</Layout>
			</Layout>
		</>
	);
};

const PrimaryTopNavLayout: React.FC = () => {
	const location = useLocation();
	const isInsilicoPage = location.pathname.startsWith("/personas");

	return (
		<>
			<TopNavbar />
			<Layout>
				{!isInsilicoPage && <SideNavbar />}
				<Layout>
					<Content className="app-content">
						<RoutesConfig />
					</Content>
				</Layout>
			</Layout>
		</>
	);
};

export default AppContent;
